<template>
    <div
        ref="utterance-group"
        class="utterances-group-wrap"
    >
        <div
            class="fab-btn-wrap"
            :class="utteranceGroupBottomOffsetHeader > 40 ? 'floating' : ''"
        >
            <div
                v-if="!showQuestionEditor && !addBtnIsHidden"
                class="icon-wrap"
            >
              <span @click="onCreate">
                <or-icon
                    :class="isLoading ? 'loading' : ''"
                    :icon="isLoading ? 'sync': 'add'"
                />
              </span>
            </div>
        </div>
        <div
            class="utterances-group-wrap__fabrique-wrap"
            :class="showQuestionEditor && !isLoading ? 'highlighted' : ''"
        >
            <div
                v-if="showQuestionEditor && !isLoading"
                class="queston-editor-wrap"
            >
                <autosize-textarea
                    v-model="newQuestion"
                    ref="question-input"
                    :placeholder="$t('common.utterance.utterances_group.writeAPhrase')"
                    :maxlength="maxlength"
                />
            </div>
            <div>
                <div
                    v-if="showQuestionEditor && !isLoading"
                    class="toolbar"
                >
                    <or-icon-button
                        class="flat"
                        icon="close"
                        type="secondary"
                        size="small"
                        @click="closeQuestionEditor"
                    />
                    <or-icon-button
                        class="flat"
                        icon="check"
                        type="secondary"
                        size="small"
                        :disabled="confirmIsDisabled"
                        @click="onConfirm"
                    />
                </div>
            </div>
        </div>
        <div v-if="showUtterancesGroup">
            <div
                v-for="(utterance) in utterancesInIntent"
                class="utterance-wrap"
                :key="utterance.id"
            >
                <utterance
                    :value="utterance"
                    :intentType="intent.type"
                    :intent="intent.name"
                    :application="context.name"
                    @update-question="(value, autoPredictEntities) => handlePhraseUpdate(value, autoPredictEntities)"
                    @delete-question="deleteUtterance({id : utterance.id, appId : context.id})"
                    @hide-add-btn="hideAddBtn()"
                    @show-add-btn="showAddBtn()"
                />
            </div>
        </div>
        <span
            v-else-if="!showQuestionEditor"
            class="cell-placeholder"
        >
          {{ $t('common.utterance.utterances_group.noPhrases') }}
        </span>
    </div>
</template>
<script>
import { mapActions } from 'vuex'
import { table_limits as tableLimits } from '@/modules/limits'
import utterance from './utterance'
import autosizeTextarea from '../autosize_textarea'

export default {
  components: {
    utterance,
    autosizeTextarea
  },

  mounted () {
    this.setUtteranceGroupHeight()
    this.setUtteranceGroupOffsetTop()
  },

  props: {
    utterancesInIntent: Array,
    context: Object,
    intent: Object,
    scrollTop: Number,
    wrapBottomFromHeaderSpace: Number
  },

  computed: {
    maxlength () {
      return tableLimits.utterance.max + 1
    },

    showUtterancesGroup () {
      return !_.isEmpty(this.utterancesInIntent)
    },

    confirmIsDisabled () {
      return !_.inRange(this.newQuestion.length, tableLimits.utterance.min, tableLimits.utterance.max + 2)
    },

    spaceToBottom () {
      return this.utteranceGroupOffsetTop + this.utteranceGroupHeight - this.scrollTop
    },

    utteranceGroupBottomOffsetHeader () {
      return this.utteranceGroupOffsetTop + this.utteranceGroupHeight - this.scrollTop - 64
    }
  },

  data () {
    return {
      isLoading: false,
      showQuestionEditor: false,
      newQuestion: '',
      utteranceGroupHeight: Number,
      utteranceGroupOffsetTop: Number,
      addBtnIsHidden: false
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['createPair']),
    ...mapActions('knowledgebase/notifications', ['addInfo']),
    ...mapActions('knowledgebase/utterances', ['updateUtterance', 'deleteUtterance', 'getGpt3Recommendations']),

    hideAddBtn () {
      this.addBtnIsHidden = true
    },

    showAddBtn () {
      this.addBtnIsHidden = false
    },

    onCreate () {
      if (this.isLoading) {
        return
      }
      this.showQuestionEditor = true
      this.$nextTick(() => {
        this.setUtteranceGroupHeight()
      })
    },

    closeQuestionEditor () {
      this.showQuestionEditor = false
      this.newQuestion = ''
      this.$nextTick(() => {
        this.setUtteranceGroupHeight()
      })
    },

    setUtteranceGroupHeight () {
      this.utteranceGroupHeight = this.$refs['utterance-group'].clientHeight
    },

    setUtteranceGroupOffsetTop () {
      this.utteranceGroupOffsetTop = this.$refs['utterance-group'].offsetTop
    },

    async onConfirm () {
      this.isLoading = true
      this.showQuestionEditor = false
      const utterance = await this.createPair({
        target: 'utterance',
        applicationName: this.context.name,
        intentName: this.intent.name,
        intentType: this.intent.type,
        utteranceText: _.trim(this.newQuestion)
      })
      this.isLoading = false
      this.newQuestion = ''
      if (utterance) {
        this.emitRecommendationsNotification(utterance)
      }
    },

    async handlePhraseUpdate (value, autoPredictEntities) {
      const utterance = await this.updateUtterance({ appId: this.context.id, data: value, autoPredictEntities })

      this.emitRecommendationsNotification({
        ...utterance,
        intentType: this.intent.type,
        application: this.context.name,
        intent: this.intent.name
      })
    },

    emitRecommendationsNotification (utterance) {
      this.addInfo({
        titleLabel: this.$t('common.utterance.utterance.recommendations.notifications.pairAdded.label'),
        event: {
          label: this.$t('common.utterance.utterance.recommendations.notifications.pairAdded.eventLabel'),
          id: 'phrases-recommendations::show',
          param: utterance
        }
      })
    }
  },

  watch: {
    scrollTop () {
      this.setUtteranceGroupHeight()
      this.setUtteranceGroupOffsetTop()
    }
  }
}
</script>

<style lang="scss" scoped>
    .utterances-group-wrap {
        display: flex;

        justify-content: stretch;
        height: 100%;

        flex-direction: column;
        flex-grow: 1;

        .fab-btn-wrap {
            position: relative;

            &.floating {
                transition: all .2s ease;
                position: sticky;
                top: 34px;
            }

            .icon-wrap {
                position: absolute;
                top:    8px;
                right: 40px;

                &.floating {
                    position: absolute;
                    top: 76px;
                    left: calc(50% - 62px);
                }

                .material-icons {
                    font-size: 18px;
                    color: $or-grey;
                    background-color: $or-white;
                    box-shadow: 1px 2px 4px $or-grey-medium;
                    border-radius: 100%;
                    transition: all .2s ease-in;

                    &:hover {
                        cursor: pointer;
                        color: darken($or-grey, 10%);
                        border-color: darken($or-grey, 10%);
                        transition: all .2s ease;
                    }

                    &.loading {
                        @keyframes spin { 100% { transform:rotate(-360deg); } }
                        animation: spin 1s linear infinite;
                        color: $or-blue;

                        &:hover {
                            cursor: default;
                        }
                    }
                }
            }
        }

        &__fabrique-wrap {
            display: flex;
            justify-content: flex-end;

            &.highlighted {
                border: solid 1px $or-blue;
            }

            .toolbar {
                display: flex;
            }

            .queston-editor-wrap {
                width: 100%;
                padding: $sp-2;
            }
        }

        .utterance-wrap:not(:last-of-type) {
            border-bottom: solid 1px $or-grey;
        }
    }
</style>
